<script setup lang="ts">
import { useSnackbarStore } from '@/store/snackbar'
import { $signAnnotationList } from '@/api/signs'
import ability from '@/plugins/casl/ability'

interface IProps {
  modelValue: boolean,
  sign: any,
}

const props = withDefaults(defineProps<IProps>(), {
  modelValue: false,
  sign: null,
})
interface Emit {
  (e: 'update:modelValue', value: boolean): void
  (e: 'onClose'): void
  (e: 'onSubmit'): void
}

const emit = defineEmits<Emit>()

// Define the type for the injected method
type GetSignMethod = () => void

// Inject the method provided by the parent
const executeGetSign = inject<GetSignMethod>('executeGetSign')

const annotations = ref<any[]>([])

const artworkFileCurrentPage = ref<number>(1)
const loading = ref<boolean>(false)

const pdfScale = ref<number>(100)

const showAnnotationsSidebar = ref<boolean>(false)
const showAnnotationCommentDialog = ref<boolean>(false)
const parentAnnotation = ref<any>(null)

const isCanAddAnnotation = computed(() => {
  return ability.can('add', 'signartworkannot') && props.sign.is_active_campaign
})

// If prepress_proof_link provided - show all files (Artwork and Prepress Proof)
const isShowAllFiles = computed(() => {
  return props.sign?.prepress_proof_file || props.sign?.prepress_proof_file_links?.length
})

const headerHeight = computed(() => {
  // If dialog is shown
  if (!!props.modelValue) {
    const headerEl = document.querySelector('.sign-artwork-review-dialog.v-card-title') as HTMLElement
    return headerEl?.offsetHeight || 48.5
  }
  else {
    return 48.5
  }
})

const closeDialog = () => {
  emit('update:modelValue', false)
}

const onClose = async () => {
  emit('onClose')
}

const setHover = (annotation: any, status: boolean) => {
  annotation['hover'] = status
}

const getAnnotations = async () => {
  if (!props?.sign?.id) return

  loading.value = true
  try {
    const res = await $signAnnotationList({
      sign: props.sign.id
    })
    annotations.value = res.data.results
  } catch (e) {
    console.log(e)
  } finally {
    loading.value = false
  }
}

const onSubmitAnnotationComment = async () => {
  parentAnnotation.value = null
  await getAnnotations()
}

watch(() => props.modelValue, () => {
  // On show dialog
  if (props.modelValue) {
    getAnnotations()
  }
  // On hide dialog
  else {
    if (executeGetSign) {
      executeGetSign()
    }
  }
})
</script>

<template>
  <div>
    <SignArtworkReviewAnnotationCommentDialog
      v-model="showAnnotationCommentDialog"
      :parent-annotation="parentAnnotation"
      @onSubmit="onSubmitAnnotationComment"
    />
    <CustomVDialog
      :model-value="modelValue"
      fullscreen
      persistent
      class="default-dialog sign-artwork-review-dialog"
      @after-leave="onClose"
      @update:modelValue="emit('update:modelValue')"
    >
      <SignArtworkReviewAnnotationsSidebar
        v-model="showAnnotationsSidebar"
        v-model:annotations="annotations"
        :sign="sign"
        :current-page="artworkFileCurrentPage"
        @onSetHover="setHover($event.annotation, $event.status)"
        @onComment="parentAnnotation = $event; showAnnotationCommentDialog = true"
      />

      <VBtn
        variant="elevated"
        density="compact"
        icon="tabler-x"
        :disabled="!!loading"
        class="v-dialog-close-btn"
        style="z-index: 1005"
        @click="closeDialog"
      />

      <VCard
        :style="{
          '--header-height': `${headerHeight}px`,
        }"
      >
        <template #title>
          <div class="d-flex align-center justify-space-between flex-wrap mt-1">
            <div class="d-flex align-center flex-wrap">
              {{ sign?.short_name }}
              Artwork Review
              <VBtn
                v-if="isCanAddAnnotation"
                icon="tabler-info-circle"
                size=""
                variant="elevated"
                density="compact"
                class="rounded-sm ml-1"
                color="info"
              >
                <VIcon
                  size="22"
                  icon="tabler-info-circle"
                />
                <VMenu
                  activator="parent"
                  location="right"
                  open-on-hover
                  open-delay="100"
                  close-delay="0"
                  offset="5"
                  class="default-tooltip"
                >
                  <span class="px-2">
                    To add an annotation right-click on the PDF
                  </span>
                </VMenu>
              </VBtn>
            </div>
            <div class="d-flex align-center flex-wrap">
              <VSlider
                v-model="pdfScale"
                :max="500"
                :min="100"
                :step="10"
                label="Scale, %"
                hide-details
                class="sign-artwork-review-scale-slider"
              >
                <template v-slot:append>
                  <VTextField
                    v-model="pdfScale"
                    type="number"
                    density="compact"
                    hide-details
                    variant="outlined"
                    class="pa-1 sign-artwork-review-scale-text-field"
                  />
                </template>
              </VSlider>
              <VBtn
                v-if="isShowAllFiles"
                size="small"
                class="my-1"
                color="secondary"
                append-icon="mdi-menu"
                @click="showAnnotationsSidebar = true"
              >
                Annotations
              </VBtn>
            </div>
          </div>
        </template>
        <VCardText>
          <VOverlay
            v-model="loading"
            persistent
            absolute
            contained
            class="align-center justify-center"
          >
            <VProgressCircular
              indeterminate
              color="primary"
            />
          </VOverlay>
          <VRow>
            <VCol
              cols="12"
              :md="isShowAllFiles ? 12 : 8"
              class="scrollable-column"
            >
              <SignArtworkReviewMainBlock
                v-model:annotations="annotations"
                v-model:artwork-file-current-page="artworkFileCurrentPage"
                :sign="sign"
                :pdf-scale="pdfScale"
                :header-height="headerHeight"
                @getAnnotations="getAnnotations"
                @onSetHover="setHover($event.annotation, $event.status)"
              />
            </VCol>
            <VCol
              v-if="!isShowAllFiles"
              cols="12"
              md="4"
              class="scrollable-column"
            >
              <SignArtworkReviewAnnotationsBlock
                v-model:annotations="annotations"
                :sign="sign"
                :current-page="artworkFileCurrentPage"
                @onSetHover="setHover($event.annotation, $event.status)"
                @onComment="parentAnnotation = $event; showAnnotationCommentDialog = true"
              />
            </VCol>
          </VRow>
        </VCardText>
      </VCard>
    </CustomVDialog>
  </div>
</template>

<style lang="scss">
.sign-artwork-review-dialog {
  .sign-artwork-review-scale-slider {
    @media (min-width: 400px) {
      min-width: 350px;
    }
    @media (max-width: 399.9px) {
      min-width: calc(100vw - 60px);
    }
    .sign-artwork-review-scale-text-field {
      width: 70px;
      input.v-field__input {
        padding: 0 5px !important;
      }
    }
  }

  .scrollable-column {
    overflow-y: auto;
    // max-height: display size - header height - top and bottom dialog padding
    max-height: calc(100vh - var(--header-height) - 40px);

    // Disable scroll on smaller screens
    @media (max-width: 959.99px) {
      overflow-y: visible;
      max-height: none;
    }
  }
}
</style>
